import React, { useEffect, useRef, useState } from "react";
import BottomSheet from "./BottomSheet";
import { PROFILE_ICONS } from "./profileMenuIcons";
import { TIER_COLORS, type FeeTierStatus } from "./FeeTierSheet";
import { launchOfferEnds } from "./FeeTierOffer";
import NotificationsOffPopup from "./NotificationsOffPopup";
import VenueSearchScreen, { VENUE_PIN } from "./VenueSearchScreen";
import { venuesApi } from "../services/venues";
import { instagramVerifiedAs, talentTickEarned } from "../utils/talentTick";
import CountryFlag from "./CountryFlag";
import CountrySheet from "./CountrySheet";
import ProfileMeta, { countryName } from "./ProfileMeta";
import { prepareVenueLocation } from "../lib/venueLocation";
import { FieldStatus } from "./FieldStatus";
import { FaCamera, FaTrash } from "react-icons/fa";
import { currencySymbol } from "../utils/currency";
import { PAYIN_CURRENCIES, PAYIN_CURRENCY_FLAGS, PayinCurrency } from "../utils/payinConfig";
import { sanitizeInstagramHandle } from "../utils/instagramHandle";
import InstagramGlyph from "./InstagramGlyph";
import { MdVerified } from "react-icons/md";
import { startInstagramLogin, onInstagramBrowserClosed } from "../lib/instagramLogin";
import type { IgVerifyStatus } from "../lib/instagramLogin";
import type { User } from "../types/User";
import type { AvatarCropper } from "../hooks/useAvatarCropper";
import type { ProfileAutosave } from "../hooks/useProfileAutosave";
import "./EditProfileSheet.css";
import { SHOW_TIERS } from "../lib/tiersPaused";
import { t } from "../i18n";
import { T } from "../i18n/T";
import { useFitText } from "../hooks/useFitText";

// The tier card's medal (drawn; it wears the tier's colour).
const LOCK_ICON = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2.5" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const MEDAL_ICON = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="14" r="6" />
    <path d="M8.5 3.5 12 8l3.5-4.5" />
  </svg>
);

// The Edit profile drawer (founder pick B, build 44): the photo big on top with the
// name, email and "Preview my public page" under it (1.5.2), the Tier Level card
// while tiers show, then artist name, Instagram, venue, bio, currency and saved
// links — every field saves itself. Lifetime Earnings left for the Profile
// list's "Stats" row (founder, 1.5.2). Split
// out of pages/ProfilePage.tsx; the field values stay on the page because the
// header and the preview read them.

type Props = {
  open: boolean;
  onClose: () => void;
  /** null = no tier to show (tiers off for this talent) → the single earnings row. */
  feeTier: FeeTierStatus | null;
  onOpenTier: () => void;
  /** "Preview my public page" under the photo: leaves the drawer for the fan's view. */
  onPreview: () => void;
  /** "Preview my public page" hops: this talent hasn't previewed yet. */
  previewHint?: boolean;
  /** No bank connected yet (founder, build 44): the tier stays hidden — its
      card says "Connect Bank" and opens it. */
  moneyLocked?: boolean;
  onConnectBank?: () => void;
  profile: User;
  /** How the last Instagram round trip ended (?igVerify=… on the way back). */
  igStatus?: IgVerifyStatus | null;
  /** Both Instagram switches on: Verify — and a tap on the Instagram box —
      open the Connect Instagram screen first (founder, build 52); nothing is typed. */
  onConnectInstagram?: () => void;
  avatar: AvatarCropper;
  autosave: ProfileAutosave;
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  bio: string;
  setBio: React.Dispatch<React.SetStateAction<string>>;
  venueName: string;
  setVenueName: React.Dispatch<React.SetStateAction<string>>;
  currency: PayinCurrency;
  handleCurrencyChange: (next: PayinCurrency) => Promise<void>;
  instagram: string;
  setInstagram: React.Dispatch<React.SetStateAction<string>>;
  links: string[];
  handleChangeLink: (index: number, value: string) => void;
  handleRemoveLink: (index: number) => void;
  saveLinks: (linksArr: string[]) => Promise<void>;
};

export default function EditProfileSheet({
  open,
  onClose,
  feeTier,
  onOpenTier,
  onPreview,
  previewHint = false,
  moneyLocked = false,
  onConnectBank,
  profile,
  igStatus = null,
  onConnectInstagram,
  avatar,
  autosave,
  name,
  setName,
  bio,
  setBio,
  venueName,
  setVenueName,
  currency,
  handleCurrencyChange,
  instagram,
  setInstagram,
  links,
  handleChangeLink,
  handleRemoveLink,
  saveLinks,
}: Props) {
  // The name under the photo: its size when it fits, smaller for a long one
  // (down to 15 px), then "…" (founder, 1.5.2 b48).
  const heroName = name.trim() || profile.name;
  const heroNameRef = useRef<HTMLDivElement>(null);
  // Re-fit on open: a closed drawer has no width to fit to.
  useFitText(heroNameRef, `${heroName}|${open}`, 15);
  const { avatarSrc, defaultAvatarPath, handleAvatarClick, fileInputRef, handleAvatarChange, showPhotoAccessPopup, setShowPhotoAccessPopup } = avatar;
  const { fieldStatus, saveTextField, blurOnEnter, setSaving, markSaved, setSaveError, NETWORK_ERR } = autosave;

  // Venue: Google venue search while the server has it on — the website
  // since 10/23, the phone apps from 1.5.3. Off, it is the plain text box.
  const venueSearch = !!profile.venueSearchEnabled;
  const [venueSearchOpen, setVenueSearchOpen] = useState(false);
  const [venuePlaceId, setVenuePlaceId] = useState<string | null>(profile.venuePlaceId || null);
  useEffect(() => setVenuePlaceId(profile.venuePlaceId || null), [profile.venuePlaceId]);
  // Closing Edit profile closes the search with it; reopening starts on the sheet.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (!open) setVenueSearchOpen(false);
  }, [open]);
  // Nearby first (founder, 2026-10-29): the first tap asks for location —
  // the system prompt, before the search and its keyboard — then the search
  // opens straight away; every later tap opens it at once (lib/venueLocation).
  const venueAsking = useRef(false);
  const openVenueSearch = () => {
    if (venueAsking.current) return;
    venueAsking.current = true;
    prepareVenueLocation().then(() => {
      venueAsking.current = false;
      if (openRef.current) setVenueSearchOpen(true);
    });
  };

  // A pick, a typed name or "Remove": shown at once, saved right after; a
  // failed save puts the old venue back with the usual "couldn't save" line.
  const saveVenue = async (venue: { placeId?: string; name: string; session?: string }) => {
    setVenueSearchOpen(false);
    const before = { name: venueName, placeId: venuePlaceId };
    setVenueName(venue.name);
    setVenuePlaceId(venue.placeId || null);
    setSaving("venueName");
    try {
      const saved = await venuesApi.save(venue);
      setVenueName(saved.venueName);
      setVenuePlaceId(saved.venuePlaceId);
      markSaved("venueName");
      window.dispatchEvent(new CustomEvent("profile:patched", { detail: { venueName: saved.venueName } }));
    } catch {
      setVenueName(before.name);
      setVenuePlaceId(before.placeId);
      setSaveError("venueName", NETWORK_ERR);
    }
  };

  // The flag: shown at once, saved right after, the old one back if that fails.
  const [countryCode, setCountryCode] = useState<string | null>(profile.countryCode || null);
  useEffect(() => setCountryCode(profile.countryCode || null), [profile.countryCode]);
  const [countryOpen, setCountryOpen] = useState(false);
  const saveCountry = async (code: string) => {
    setCountryOpen(false);
    const before = countryCode;
    setCountryCode(code);
    if (!(await autosave.putProfileField("countryCode", { countryCode: code }))) setCountryCode(before);
  };

  // "Continue with Instagram". The badge only counts while Meta's handle is
  // still the handle on the profile — the server voids it on any other edit.
  const [igOpening, setIgOpening] = useState(false);
  const igVerifiedAs = instagramVerifiedAs(profile);
  const igTickEarned = talentTickEarned(profile);

  // One line when the round trip did not verify — the same words the QR-tab
  // card uses. Cancelled and a personal account say nothing (founder cut the
  // explaining line 2026-09-24); the typed field is right here anyway. A
  // personal account gets "Verify with a DM" over this drawer instead while
  // the server has it on (ProfilePage → TalentIgConnect, 1.5.3).
  const igNote =
    igStatus === "taken"
      ? t("editProfileSheet.thisInstagramIsAlready")
      : igStatus === "failed" || igStatus === "expired"
        ? t("editProfileSheet.thatDidnTGo")
        : "";

  useEffect(() => onInstagramBrowserClosed(() => setIgOpening(false)), []);

  // Verify, while Instagram's sign-in is on for this talent. No (i) about
  // account types (founder, 10/31: "DJs shouldn't care, that's under the
  // hood"): a personal account goes on to "send us a message" by itself
  // (ProfilePage → TalentIgConnect). While Verify shows, the button carries
  // the one green rosette; the label's "approved" rosette waits (two read odd).
  const showVerify = !igVerifiedAs && !!profile.instagramLoginEnabled;

  const verifyWithInstagram = async () => {
    if (onConnectInstagram) return onConnectInstagram();
    setIgOpening(true);
    const started = await startInstagramLogin("profile");
    if (!started) setIgOpening(false);
  };
  return (
    <>
      {/* ── Edit profile drawer — name / bio / venue / currency / instagram / links.
          The fields stay inside .profile-form so their input styles still apply. ── */}
      <BottomSheet open={open} title={t("editProfileSheet.editProfile")} icon={PROFILE_ICONS.edit} onClose={onClose}>
      {/* The photo, big and centred, the name under it (founder pick B, build 44).
          Tapping the photo runs the same picker → cropper → upload path; the
          popup covers the OS photo-permission-denied case. */}
      <div className="ep-hero">
        <button
          type="button"
          className="profile-edit-photo-wrap"
          onClick={handleAvatarClick}
          title={t("editProfileSheet.changePhoto")}
        >
          <img
            src={avatarSrc}
            alt={t("editProfileSheet.profileAvatar")}
            className="profile-avatar-image"
            onError={(e) => (e.currentTarget.src = defaultAvatarPath)}
          />
          <span className="avatar-upload-label" aria-hidden="true">
            <FaCamera className="camera-icon" size={20} />
          </span>
        </button>
        <div className="ep-hero-name" ref={heroNameRef} title={heroName}>{heroName}</div>
        {/* The email moved here from the Profile tab's top row (1.5.2). */}
        {profile.email && <div className="ep-hero-email">{profile.email}</div>}
        <ProfileMeta countryCode={countryCode} joinedAt={profile.createdAt} />
        {/* Hops until the talent's first preview (founder, build 44 — lib/welcomeHints). */}
        <button type="button" className={`ep-hero-preview${previewHint ? " ep-hero-preview--hop" : ""}`} onClick={onPreview}>
          {t("profileHeadRow.previewMyPublicPage")} ›
        </button>
        <input
          ref={fileInputRef}
          type="file"
          id="avatar"
          accept="image/*"
          // `multiple` switches iOS Safari from the legacy
          // UIImagePickerController (which shows a preview-with-checkmark
          // step before confirming) to PHPicker, which auto-confirms on
          // tap. We still only use the first file in handleAvatarChange,
          // so behavior is unchanged for the user other than skipping the
          // extra preview step.
          multiple
          onChange={handleAvatarChange}
          style={{ display: "none" }}
        />
        <NotificationsOffPopup
          open={showPhotoAccessPopup}
          onClose={() => setShowPhotoAccessPopup(false)}
          emoji="📷"
          title={t("editProfileSheet.photoAccessIsOff")}
          sub={t("editProfileSheet.allowPhotoAccessTo")}
        />
      </div>

      {/* Tier Level card — shown only while tiers show (paused since 2026-10-22,
          lib/tiersPaused). Lifetime Earnings left this drawer for the Profile
          list's "Stats" row (founder, 1.5.2). */}
      {SHOW_TIERS && moneyLocked ? (
        // No bank yet (founder, build 44): a lock and "Connect Bank" where the tier goes.
        <div className="ep-stat-cards ep-stat-cards--one">
          <button type="button" className="ep-stat-card ep-stat-card--locked" onClick={onConnectBank}>
            <span className="ep-stat-ic" aria-hidden="true">{MEDAL_ICON}</span>
            <span className="ep-stat-text">
              <span className="ep-stat-k">{t("editProfileSheet.tierLevel")}</span>
              <span className="ep-stat-lock">{LOCK_ICON}{t("connectBankSheet.connectBank")}</span>
            </span>
          </button>
        </div>
      ) : SHOW_TIERS && feeTier?.tier ? (
        <div className="ep-stat-cards ep-stat-cards--one">
          <button type="button" className="ep-stat-card" onClick={onOpenTier}>
            {/* The medal wears the tier's own colour (green while the launch offer runs). */}
            <span className="ep-stat-ic" aria-hidden="true" style={{ color: launchOfferEnds(feeTier) ? "#24C961" : TIER_COLORS[feeTier.tier.key] || "#24C961" }}>
              {MEDAL_ICON}
            </span>
            <span className="ep-stat-text">
              <span className="ep-stat-k">{t("editProfileSheet.tierLevel")}</span>
              {/* While the launch offer runs (founder 1.5.1 #12): "Launch". The
                  share is what they really keep — a launch offer or custom
                  rate, else the rung's. */}
              <span className="ep-stat-line">
                <span className="ep-stat-v">
                  {launchOfferEnds(feeTier) ? t("editProfileSheet.launch") : feeTier.overrideActive ? t("editProfileSheet.custom") : feeTier.tier.name}
                </span>
                <span className="ep-stat-d">
                  {t("editProfileSheet.percentYours", { keepPct: 100 - (feeTier.overrideActive ? 100 - feeTier.effectiveKeepPct : feeTier.tier.feePct) })}
                </span>
              </span>
            </span>
          </button>
        </div>
      ) : null}
      {/* Personal details group — boxed (founder-approved Style B).
          The card is a pure wrapper: every field inside is unchanged. */}
      <div className="profile-group-card profile-group-card--fields">
      <label>{t("editProfileSheet.artistName")}</label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value.slice(0, 30))}
        onBlur={() => saveTextField("name", "name", name, profile.name || "")}
        onKeyDown={blurOnEnter}
        enterKeyHint="done"
        maxLength={30}
      />
      <FieldStatus status={fieldStatus.name} />

      {/* Instagram is the second row, right under the artist name (founder,
          2026-10-04): the coloured glyph, "Instagram", then "Verify" directly to
          its right on the same line ("Loading" while Instagram opens), or the
          green "Verified" once Meta vouched.
          The button is a sibling of the label, not inside it: a button inside
          a label would make a tap on the word "Instagram" press it. */}
      <div className="ig-label-row">
      <label>
        {/* Instagram's own colours on the glyph, left of the word (founder, 2026-10-04). */}
        <InstagramGlyph className="ig-label-glyph" />
        {t("editProfileSheet.instagram")}
        {/* Once Meta vouched, the green "Verified" chip beside the label carries
            the badge, so the label's own rosette steps aside — one badge, not two. */}
        {instagram.trim() && igTickEarned && !igVerifiedAs && !showVerify && (
          // The verified-style badge from the founder's screenshot: a scalloped
          // Brand Green rosette with a dark tick. A true circle (founder,
          // 1.5.2): 12 equal lobes, points at 10.5 and dips at 9.2 around
          // (12,12), every 15 degrees — the old hand-drawn path pinched its
          // lower-right and upper-left quarters. The tick sits 0.4 above the
          // middle so its weight reads centred.
          <svg className="verified-badge" width="19" height="19" viewBox="-1 -1 26 26" aria-label={t("editProfileSheet.connected")} role="img">
            <path
              fill="#24C961"
              stroke="#24C961"
              strokeWidth="1.8"
              strokeLinejoin="round"
              d="M12 1.5L14.38 3.11L17.25 2.91L18.51 5.49L21.09 6.75L20.89 9.62L22.5 12L20.89 14.38L21.09 17.25L18.51 18.51L17.25 21.09L14.38 20.89L12 22.5L9.62 20.89L6.75 21.09L5.49 18.51L2.91 17.25L3.11 14.38L1.5 12L3.11 9.62L2.91 6.75L5.49 5.49L6.75 2.91L9.62 3.11Z"
            />
            <path fill="none" stroke="#0a0d14" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M8 11.9l2.7 2.7L16 9.3" />
          </svg>
        )}
      </label>
      {igVerifiedAs ? (
        // Verified: just the badge and the word, in green (founder, 2026-10-04).
        <span className="ig-verified-chip" role="status" title={t("editProfileSheet.verifiedWithInstagram", { igVerifiedAs })}>
          <MdVerified aria-hidden />{t("editProfileSheet.verified")}
        </span>
      ) : (
        showVerify && (
          <>
            {/* The green rosette rides on the button so it reads as "do this,
                get the badge" (founder, 2026-09-30). */}
            <button type="button" className="ig-verify-btn" onClick={verifyWithInstagram} disabled={igOpening}>
              {igOpening ? t("editProfileSheet.loading") : t("editProfileSheet.verify")}
              <MdVerified className="ig-verify-btn-badge" aria-hidden />
            </button>
          </>
        )
      )}
      </div>
      <div className={`instagram-input-wrapper fixed-at instagram-input-wrapper--glyph${onConnectInstagram ? " ig-box--connect" : ""}`}>
        {/* "@handle" reads as ONE string — the @ wears the input's own font and
            colour and sits flush against the typed handle. The glyph moved up
            to the label (founder, 2026-10-04). */}
        <span className="instagram-prefix" aria-hidden="true">
          <span className="instagram-at">@</span>
        </span>
        <input
          className="instagram-input"
          placeholder={t("editProfileSheet.username")}
          value={instagram}
          readOnly={!!onConnectInstagram}
          tabIndex={onConnectInstagram ? -1 : undefined}
          onChange={(e) => setInstagram(sanitizeInstagramHandle(e.target.value))}
          onBlur={() => {
            if (!onConnectInstagram) saveTextField("instagram", "instagram", sanitizeInstagramHandle(instagram), (profile.instagram || "").replace(/^@+/, ""));
          }}
          onKeyDown={blurOnEnter}
          enterKeyHint="done"
          inputMode="text"
          autoComplete="off"
        />
        {/* Both switches on: the whole box is the Verify button (founder 10/30:
            "leave the text box, but when they press it's the same as if they
            press verify") — nothing typed is saved. */}
        {onConnectInstagram && (
          <button type="button" className="ig-box-tap" aria-label={igVerifiedAs ? t("editProfileSheet.instagram") : t("editProfileSheet.verify")} onClick={onConnectInstagram} />
        )}
      </div>
      <FieldStatus status={fieldStatus.instagram} />
      {/* "Continue with Instagram" (THE IG GATE's verified handle). Only the
          talent sees this, and only while the feature is switched on for them
          — the row is invisible to everyone else. The rosette above is the
          founder's existing "handle saved" mark and is deliberately left
          alone; whether it should mean VERIFIED is a founder call for the day
          this goes live for everyone. */}
      {!igVerifiedAs && profile.instagramLoginEnabled && igNote && (
        <p className="ig-verify-row ig-verify-row--note" role="status">{igNote}</p>
      )}


      {/* Venue above bio (founder, build 44): who, where, then the vibe. */}
      <label>{t("editProfileSheet.venueName")}</label>
      {venueSearch ? (
        <button type="button" className="venue-box" onClick={openVenueSearch}>
          <span className={`venue-box-pin${venuePlaceId ? " venue-box-pin--on" : ""}`}>{VENUE_PIN}</span>
          <span className={`venue-box-text${venueName ? "" : " venue-box-text--empty"}`}>
            {venueName || t("editProfileSheet.whereDoYouUsually")}
          </span>
          <span className="venue-box-chev" aria-hidden="true">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </span>
        </button>
      ) : (
        <input
          placeholder={t("editProfileSheet.whereDoYouUsually")}
          value={venueName}
          onChange={(e) => setVenueName(e.target.value)}
          onBlur={() => saveTextField("venueName", "venueName", venueName, (profile as any).venueName || "")}
          onKeyDown={blurOnEnter}
          enterKeyHint="done"
          maxLength={50}
        />
      )}
      <FieldStatus status={fieldStatus.venueName} />

      {/* Counter lives next to the label (founder decision) — inside the
          box it was eating typing room. */}
      <label>
        <T k="editProfileSheet.bio" vars={{ length: bio.length }} tags={{ 0: (c) => <span className="bio-char-count">{c}</span> }} />
      </label>
      <input
        className="bio-input"
        placeholder={t("editProfileSheet.whatSYourVibe")}
        value={bio}
        onChange={(e) => setBio(e.target.value.slice(0, 40))}
        onBlur={() => saveTextField("bio", "bio", bio, profile.bio || "")}
        onKeyDown={blurOnEnter}
        enterKeyHint="done"
        maxLength={40}
      />
      <FieldStatus status={fieldStatus.bio} />

      {/* Country and currency share a row (founder, 2026-11-03). */}
      <div className="ep-two-col">
        <div>
          <label>{t("profileMeta.country")}</label>
          <button type="button" className="ep-country-box" onClick={() => setCountryOpen(true)}>
            {countryCode && <CountryFlag code={countryCode} />}
            <span className={`ep-country-text${countryCode ? "" : " ep-country-text--empty"}`}>
              {countryCode ? countryName(countryCode) : t("profileMeta.addCountry")}
            </span>
            <span className="ep-country-chev" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </span>
          </button>
          <FieldStatus status={fieldStatus.countryCode} />
        </div>
        <div>
          <label>{t("editProfileSheet.currency")}</label>
          <select
            className="profile-select"
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value as PayinCurrency)}
          >
            {PAYIN_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {PAYIN_CURRENCY_FLAGS[c]} {c.toUpperCase()} ({currencySymbol(c)})
              </option>
            ))}
          </select>
          <FieldStatus status={fieldStatus.currency} />
        </div>
      </div>

      {/* "+ Add link" parked (founder) — restore the button here
          with handleAddLink when links come back. Existing saved links stay
          editable below so nobody's data is stranded. */}

      {links.length > 0 && (
        <>
          <label style={{ marginTop: "0.75rem" }}>{t("editProfileSheet.linksUpTo")}</label>
          <div className="links-list">
            {links.map((val, i) => {
              const filled = val.trim().length > 0;
              return (
                <div className="links-row" key={i}>
                  <input
                    className="link-input"
                    placeholder={t("editProfileSheet.httpsExampleComAnything")}
                    value={val}
                    onChange={(e) => handleChangeLink(i, e.target.value)}
                    onBlur={() => saveLinks(links)}
                    onKeyDown={blurOnEnter}
                    inputMode="url"
                  />
                  <button
                    type="button"
                    className="remove-link-btn"
                    onClick={() => handleRemoveLink(i)}
                    title={t("editProfileSheet.removeThisLink")}
                    disabled={!filled && links.length === 1}
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              );
            })}
          </div>
          <FieldStatus status={fieldStatus.links} />
        </>
      )}
      </div>
      {/* --- /Personal details group --- */}
      </BottomSheet>
      <CountrySheet open={open && countryOpen} selectedCode={countryCode} onPick={saveCountry} onClose={() => setCountryOpen(false)} />
      {open && venueSearchOpen && (
        <VenueSearchScreen
          current={{ name: venueName, placeId: venuePlaceId }}
          onPick={saveVenue}
          onRemove={() => saveVenue({ name: "" })}
          onClose={() => setVenueSearchOpen(false)}
        />
      )}
    </>
  );
}
